import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

export default function WhotTournamentLobby() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [name, setName] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [entryWager, setEntryWager] = useState(0);
  
  // House rules state
  const [oneRound, setOneRound] = useState(true);
  const [generalMarket14, setGeneralMarket14] = useState(true);
  const [whotEnabled, setWhotEnabled] = useState(true);
  const [pick2, setPick2] = useState(true);
  const [pick3On5, setPick3On5] = useState(true);
  const [holdOn1, setHoldOn1] = useState(true);
  const [suspension8, setSuspension8] = useState(true);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/whot/tournaments');
      setTournaments(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTournaments();
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Arena name is required.');
      return;
    }
    const safeDuration = Math.min(120, Math.max(10, durationMinutes));
    const safeWager = Math.max(0, Number(entryWager));

    try {
      setLoading(true);
      const res = await api.post('/whot/tournaments', { 
        name, 
        durationMinutes: safeDuration, 
        entryWager: safeWager,
        houseRules: { oneRound, generalMarket14, whotEnabled, pick2, pick3On5, holdOn1, suspension8 }
      });
      navigate(`/whot/tournaments/${res.data.id}`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create tournament');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (tournamentId) => {
    try {
      setLoading(true);
      const res = await api.post(`/whot/tournaments/${tournamentId}/join`);
      navigate(`/whot/tournaments/${res.data.id}`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to join tournament');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tournamentId) => {
    if (!window.confirm('Are you sure you want to delete this Arena? All wagers will be refunded.')) return;
    try {
      setLoading(true);
      await api.post(`/whot/tournaments/${tournamentId}/cancel`);
      loadTournaments();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete tournament');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="dashboard-content">
        <div className="chess-lobby-header">
          <div className="header-left">
            <div className="header-icon">🃏</div>
            <div>
              <h1 className="page-title">Whot Arena</h1>
              <p className="page-subtitle">Play continuously, crush your opponents, and collect points!</p>
            </div>
          </div>
          <button className="back-btn" onClick={() => navigate('/whot-online')}>
            ← Back to Whot
          </button>
        </div>

        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button className="error-close" onClick={() => setError('')}>×</button>
          </div>
        )}

        {user?.role === 'admin' && (
          <div className="quick-actions-card">
            <div className="section-header">
              <h2 className="section-title">Create Arena</h2>
              <p className="section-subtitle">Set up a continuous-play Arena where the highest score wins.</p>
            </div>

            <div className="form-grid">
              <label>
                Arena name
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Midnight Blitz Arena" required />
              </label>
              <label>
                Arena Duration (10-120 min)
                <input type="number" min="10" max="120" value={durationMinutes} onChange={(e) => setDurationMinutes(Number(e.target.value) || 10)} />
              </label>
              <label>
                Entry Wager (0 for Free)
                <input type="number" min="0" value={entryWager} onChange={(e) => setEntryWager(Number(e.target.value) || 0)} />
              </label>
              
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>⚙️ House Rules</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={whotEnabled} onChange={(e) => setWhotEnabled(e.target.checked)} />
                    <span>WHOT Card (20) Enabled</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={pick2} onChange={(e) => setPick2(e.target.checked)} />
                    <span>Pick 2 (card 2)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={pick3On5} onChange={(e) => setPick3On5(e.target.checked)} />
                    <span>Pick 3 on 5</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={holdOn1} onChange={(e) => setHoldOn1(e.target.checked)} />
                    <span>Hold On (card 1)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={suspension8} onChange={(e) => setSuspension8(e.target.checked)} />
                    <span>Suspension (card 8)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={generalMarket14} onChange={(e) => setGeneralMarket14(e.target.checked)} />
                    <span>General Market (card 14)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={oneRound} onChange={(e) => setOneRound(e.target.checked)} />
                    <span>One Round (no reshuffle)</span>
                  </label>
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1', marginTop: 8, padding: '12px', background: 'rgba(255,255,255,0.02)', borderRadius: 8, fontSize: 13, color: 'rgba(255,255,255,0.5)', border: '1px dashed rgba(255,255,255,0.1)' }}>
                ℹ️ Highest point total wins the pot at the end of the duration.
              </div>
            </div>

            <button className="flappy-btn-primary" onClick={handleCreate} disabled={loading} style={{ background: '#e11d48', borderColor: '#be123c' }}>
              Launch Arena
            </button>
          </div>
        )}

        <div className="matches-section">
          {/* Active / Pending Arenas */}
          <div className="section-header">
            <h2 className="section-title">Open Arenas</h2>
            <p className="section-subtitle">Join an active arena and immediately start playing.</p>
          </div>

          {loading && <div>Loading arenas…</div>}
          {!loading && tournaments.filter(t => t.status !== 'completed').length === 0 && (
            <div style={{ padding: '24px', background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: '1px dashed rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', textAlign: 'center', marginBottom: 40 }}>
              No open arenas at the moment. Create one above!
            </div>
          )}

          <div className="matches-list">
            {tournaments.filter(t => t.status !== 'completed').map((tournament) => (
              <div key={tournament.id} className="match-card chess-tournament-card">
                <div className="match-card-main">
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <div className="match-title">{tournament.name}</div>
                      <span style={{ 
                        fontSize: 10, 
                        padding: '2px 8px', 
                        borderRadius: 4, 
                        textTransform: 'uppercase',
                        background: tournament.status === 'active' ? '#10b981' : '#f59e0b',
                        color: 'white'
                      }}>
                        {tournament.status}
                      </span>
                    </div>
                    <div className="match-meta">
                      {tournament.player_count || 0} players • {tournament.duration_minutes || 30} mins • Wager: {tournament.entry_wager} wkp
                    </div>
                  </div>
                  <button className="action-btn play" onClick={() => navigate(`/whot/tournaments/${tournament.id}`)}>
                    View Arena
                  </button>
                  {tournament.status === 'pending' && Number(tournament.creator_id) === Number(user?.id) && (
                    <button 
                      className="action-btn" 
                      onClick={() => handleDelete(tournament.id)}
                      style={{ background: '#7f1d1d', color: 'white', marginLeft: 8 }}
                    >
                      🗑 Delete
                    </button>
                  )}
                </div>
                <div className="match-card-footer">
                  <div>Created by {tournament.creator_username || 'Unknown'}</div>
                  {user?.role !== 'admin' && (
                    <button className="action-btn watch" onClick={() => handleJoin(tournament.id)}>
                      Join ({tournament.entry_wager} wkp)
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Past Arenas Section */}
          {tournaments.some(t => t.status === 'completed') && (
            <div style={{ marginTop: 64 }}>
              <div className="section-header">
                <h2 className="section-title">Past Arenas</h2>
                <p className="section-subtitle">Historical records of finished tournaments and winners.</p>
              </div>
              <div className="matches-list" style={{ opacity: 0.7 }}>
                {tournaments.filter(t => t.status === 'completed').map((tournament) => (
                  <div key={tournament.id} className="match-card chess-tournament-card" style={{ filter: 'grayscale(0.5)' }}>
                    <div className="match-card-main">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                          <div className="match-title">{tournament.name}</div>
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', background: '#475569', color: 'white' }}>
                            FINISHED
                          </span>
                        </div>
                        <div className="match-meta">
                           Ended • Winner: {tournament.winner_username || 'None'}
                        </div>
                      </div>
                      <button className="action-btn watch" onClick={() => navigate(`/whot/tournaments/${tournament.id}`)}>
                        Results
                      </button>
                    </div>
                    <div className="match-card-footer">
                      <div>Final Player Count: {tournament.player_count || 0}</div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{new Date(tournament.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
